import React from 'react';
import styled from 'styled-components';
import { colors, chartColors } from './colors';
import { Skeleton } from '@mui/material';

// Chart Container Component
export const ChartContainer = styled.div`
  background: ${colors.white};
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
  min-height: 400px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    border-radius: 20px 20px 0 0;
    background: linear-gradient(90deg, #667eea, #764ba2);
    z-index: 1;
  }
  
  &:hover {
    transform: translateY(-8px) scale(1.01);
    box-shadow: 
      0 20px 40px rgba(0, 0, 0, 0.15),
      0 0 0 1px rgba(0, 0, 0, 0.1);
  }
  
  .chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    position: relative;
    z-index: 2;
    margin-top: 0.5rem; /* Add space for the color line */
  }
  
  .chart-title {
    font-size: 1.5rem;
    font-weight: 700;
    background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  .chart-subtitle {
    font-size: 0.875rem;
    color: #64748b;
    margin: 0.5rem 0 0 0;
    font-weight: 500;
    opacity: 0.8;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    min-height: 250px;
    
    .chart-title {
      font-size: 1.1rem;
    }
    
    .chart-subtitle {
      font-size: 0.8rem;
    }
  }
`;

// Custom Tooltip Component
export const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <TooltipContainer>
        <TooltipLabel>{label}</TooltipLabel>
        {payload.map((entry, index) => (
          <TooltipItem key={index} color={entry.color}>
            <span>{entry.dataKey}:</span>
            <strong>{entry.value}</strong>
          </TooltipItem>
        ))}
      </TooltipContainer>
    );
  }
  return null;
};

const TooltipContainer = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.inputBorderWhite};
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
`;

const TooltipLabel = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  color: ${colors.text};
  font-size: 0.875rem;
`;

const TooltipItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  
  span {
    color: ${colors.textMed};
    margin-right: 8px;
    font-size: 0.8rem;
  }
  
  strong {
    color: ${colors.text};
    font-weight: 600;
    font-size: 0.875rem;
  }
`;

// Custom Pie Chart Tooltip
export const CustomPieTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    return (
      <TooltipContainer>
        <TooltipLabel>{data.name}</TooltipLabel>
        <TooltipItem>
          <span>Count:</span>
          <strong>{data.value}</strong>
        </TooltipItem>
        <TooltipItem>
          <span>Percentage:</span>
          <strong>{((data.value / data.payload.total) * 100).toFixed(1)}%</strong>
        </TooltipItem>
      </TooltipContainer>
    );
  }
  return null;
};

// Loading Skeleton Component
export const ChartSkeleton = () => (
  <ChartContainer>
    <div className="chart-header">
      <div>
        <Skeleton variant="text" width={150} height={24} />
        <Skeleton variant="text" width={200} height={16} />
      </div>
    </div>
    <Skeleton variant="rectangular" height={300} />
  </ChartContainer>
);

// Enhanced Legend Component for Classification
export const ClassificationLegend = ({ data }) => {
  const total = data?.reduce((sum, item) => sum + item.count, 0) || 0;
  
  return (
    <LegendContainer>
      {data?.map((item, index) => (
        <LegendItem key={item._id}>
          <LegendColor color={chartColors.leadClassification[item._id]} />
          <LegendText>
            <span className="label">{item._id}</span>
            <span className="value">{item.count} ({((item.count / total) * 100).toFixed(1)}%)</span>
          </LegendText>
        </LegendItem>
      ))}
    </LegendContainer>
  );
};

const LegendContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${colors.inputWhite};
  }
`;

const LegendColor = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${props => props.color};
  flex-shrink: 0;
`;

const LegendText = styled.div`
  display: flex;
  flex-direction: column;
  
  .label {
    font-size: 14px;
    font-weight: 500;
    color: ${colors.text};
    text-transform: capitalize;
  }
  
  .value {
    font-size: 12px;
    color: ${colors.textMed};
  }
`;

// Task Legend Component
export const TaskLegend = ({ data }) => {
  const total = data?.reduce((sum, item) => sum + item.value, 0) || 0;
  
  return (
    <TaskLegendContainer>
      {data?.map((item, index) => (
        <TaskLegendItem key={index}>
          <TaskLegendColor color={item.color} />
          <TaskLegendText>
            <span className="label">{item.name}</span>
            <span className="value">{item.value} ({((item.value / total) * 100).toFixed(1)}%)</span>
          </TaskLegendText>
        </TaskLegendItem>
      ))}
    </TaskLegendContainer>
  );
};

const TaskLegendContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 1rem;
`;

const TaskLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px;
  border-radius: 4px;
`;

const TaskLegendColor = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background-color: ${props => props.color};
  flex-shrink: 0;
`;

const TaskLegendText = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  
  .label {
    font-size: 13px;
    font-weight: 500;
    color: ${colors.text};
  }
  
  .value {
    font-size: 12px;
    color: ${colors.textMed};
    font-weight: 600;
  }
`;

// Specialized Chart Containers with different color lines
export const LeadsReportContainer = styled(ChartContainer)`
  &::before {
    background: linear-gradient(90deg, #667eea, #764ba2);
  }
`;

export const PendingTaskContainer = styled(ChartContainer)`
  &::before {
    background: linear-gradient(90deg, #f093fb, #f5576c);
  }
`;

export const CallsReportContainer = styled(ChartContainer)`
  &::before {
    background: linear-gradient(90deg, #4facfe, #00f2fe);
  }
`;

export const ClassificationContainer = styled(ChartContainer)`
  &::before {
    background: linear-gradient(90deg, #43e97b, #38f9d7);
  }
`;

export const RevenueGraphContainer = styled(ChartContainer)`
  max-width: none;
  width: 100%;
  
  &::before {
    background: linear-gradient(90deg, #ff6b6b, #ffa726);
  }
`;

// CEO/Admin Dashboard Containers
export const EmployeePerformanceContainer = styled(ChartContainer)`
  max-width: none;
  width: 100%;
  
  &::before {
    background: linear-gradient(90deg, #667eea, #764ba2);
  }
`;

export const CompanyOverviewContainer = styled(ChartContainer)`
  &::before {
    background: linear-gradient(90deg, #f093fb, #f5576c);
  }
`;

export const PerformanceComparisonContainer = styled(ChartContainer)`
  &::before {
    background: linear-gradient(90deg, #4facfe, #00f2fe);
  }
`;

// Dashboard Grid Layout
export const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: 2.5rem;
  padding: 0;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding: 0;
  }
  
  @media (min-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 1600px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;
